import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { CheckCircleOutline as CheckIcon } from "@mui/icons-material";
import BlockIcon from "@mui/icons-material/Block";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PublicIcon from "@mui/icons-material/Public";
import { green, grey, orange } from "@mui/material/colors";
import {
  WebSearchApprovalData,
  ChatMessageWithParts,
  ContentType,
  RecencyFilter,
} from "src/views/assistant/types";
import { useQueryClient } from "react-query";
import { extractErrorDetail } from "src/helpers/apiError";
import {
  armDeliveryWatcher,
  deliverToolOutcome,
  DeliveryResult,
  retryToolOutcomeInjection,
  useDeliveryWatcher,
} from "src/reusable/Chat/ChatStreaming/delivery";
import { persistToolResponseOutcome } from "src/reusable/Chat/ChatStreaming/services/api";
import { useChatStreamStore } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useChatContext } from "../../context/ChatContext";
import { resolveOutcome } from "src/reusable/Chat/AssistantMessage/artifacts/resolveOutcome";
import { useGetExternalAction } from "src/views/assistant/services/AtomAssistActions";
import {
  approveWebSearch,
  rejectWebSearch,
  WebSearchErrorResponse,
} from "src/views/assistant/services/webSearchService";

type ResolvedResponse = NonNullable<WebSearchApprovalData["user_response"]> & {
  /** The search executed but the provider returned an error. */
  failed?: boolean;
  /** Provider failure reason (from the action record on reload). */
  error?: string;
};

interface ParsedPromptData {
  promptData: WebSearchApprovalData | null;
  existingResponse: ResolvedResponse | null;
  searchResults: Record<string, unknown> | null;
}

/** Sentinel for "no filter" (final_recency is null) in the label lookup below. */
const RECENCY_NONE = "none" as const;
type RecencyLabelKey = RecencyFilter | typeof RECENCY_NONE;
const RECENCY_OPTIONS: { value: RecencyLabelKey; label: string }[] = [
  { value: RECENCY_NONE, label: "Any time" },
  { value: "day", label: "Past day" },
  { value: "week", label: "Past week" },
  { value: "month", label: "Past month" },
  { value: "year", label: "Past year" },
];

/** Result types that mean "the search ran but there is nothing to use". */
const FAILURE_RESULT_TYPES = new Set([
  "web_search_error",
  "web_search_failed",
  "web_search_results_unavailable",
]);

const DELIVERY_FAILED_TEXT =
  "The response could not be delivered to the assistant.";

const MARKER_WRITE_FAILED_TEXT =
  "Could not record the delivery failure — reload before retrying.";

const MARKER_PERSIST_FAILED_TEXT =
  "The response could not be delivered, and recording the failure also " +
  "failed — Retry may not survive a reload.";

const SESSION_GONE_TEXT =
  "The assistant session has ended. Your response is saved and will be " +
  "included when the conversation continues.";

const DELIVERING_TEXT = "Delivering the response to the assistant…";

const RETRY_DISABLED_TEXT = " Retry is disabled for this failure.";

const PRIMARY_PERSIST_FAILED_TEXT =
  "Saved locally, but recording the outcome failed — it may reappear after " +
  "a reload.";

const CLEAR_NOT_RECORDED_TEXT =
  "Delivered, but clearing the saved failure marker didn't succeed — a " +
  "reload may wrongly offer Retry.";

const STATUS_UNAVAILABLE_TEXT =
  "Couldn't load this search request's status — reload the page to try again.";

const CANNOT_ACT_TEXT =
  "This view can't deliver approvals — open this chat from its main page.";

interface OutcomePayload {
  approved: boolean;
  finalQuery?: string;
  searchResults?: unknown;
}

/** User-voiced message for old backends and the legacy resume paths. */
function buildFallbackMessage(
  defaultQuery: string,
  outcome: OutcomePayload,
): string {
  if (!outcome.approved) return "I declined the web search.";
  const query = outcome.finalQuery || defaultQuery;
  const sr = (outcome.searchResults ?? {}) as Record<string, unknown>;
  if (FAILURE_RESULT_TYPES.has(String(sr.type))) {
    const reason =
      typeof sr.error === "string" && sr.error ? `: ${sr.error}` : "";
    return (
      `I approved the web search for "${query}", but it failed${reason}. ` +
      "Retry the search if the results are needed, or answer without it."
    );
  }
  return (
    `Here are the results of the approved web search for "${query}":\n\n` +
    String(sr.markdown ?? "")
  );
}

/**
 * Strip tool_response XML tags from text if present.
 */
function stripToolResponseTags(text: string): string {
  return text
    .replace(/^<tool_response>\s*/, "")
    .replace(/\s*<\/tool_response>$/, "");
}

/**
 * Wrap text with tool_response tags based on original text markers.
 */
function wrapWithToolResponseTags(
  newText: string,
  originalText: string,
): string {
  const hasOpenTag = originalText.includes("<tool_response>");
  const hasCloseTag = originalText.includes("</tool_response>");

  let result = newText;
  if (hasOpenTag) result = "<tool_response>" + result;
  if (hasCloseTag) result = result + "</tool_response>";
  return result;
}

/**
 * Parse web search approval data from raw text.
 * Handles both direct format and nested tool_result format.
 */
function parsePromptData(text: string): ParsedPromptData {
  try {
    const jsonText = text.includes("<tool_response>")
      ? stripToolResponseTags(text)
      : text;
    const parsed = JSON.parse(jsonText);
    const userResponse = parsed.user_response || null;

    if (parsed.type === "web_search_approval") {
      return {
        promptData: parsed as WebSearchApprovalData,
        existingResponse: userResponse,
        searchResults: parsed.search_results ?? null,
      };
    }

    // Handle nested tool_result format
    if (parsed.message_type === "tool_result" && parsed.result) {
      const innerResult =
        typeof parsed.result === "string"
          ? JSON.parse(parsed.result)
          : parsed.result;
      if (innerResult?.type === "web_search_approval") {
        return {
          promptData: innerResult as WebSearchApprovalData,
          existingResponse: innerResult.user_response || userResponse,
          // Both the server annotation and the FE patch land at the inner
          // level; the FE patch mirrors the keys onto the wrapper too.
          searchResults:
            innerResult.search_results ?? parsed.search_results ?? null,
        };
      }
    }

    return { promptData: null, existingResponse: null, searchResults: null };
  } catch {
    // JSON parse failures are expected for incomplete/malformed data during streaming
    if (import.meta.env.DEV) {
      console.debug("[WebSearchApproval] Failed to parse prompt data");
    }
    return { promptData: null, existingResponse: null, searchResults: null };
  }
}

/** True when a TOOL_RESPONSE part's text is this prompt's approval payload. */
function partMatchesAction(partText: string, actionId: number): boolean {
  return parsePromptData(partText).promptData?.action_id === actionId;
}

interface WebSearchApprovalProps {
  /** Raw text containing JSON prompt data */
  text: string;
  /** Whether the chat is still streaming */
  isChatConnected: boolean;
  /** Message ID this prompt belongs to (for updating the dialogue) */
  messageId?: string;
  /** Index of this content part within the message */
  partIndex?: number;
}

/**
 * WebSearchApproval Component
 *
 * Renders an interactive approval form for web search requests.
 * Implements DOE 810 compliance by requiring explicit user approval
 * before any data is sent to external search services.
 */
const WebSearchApproval = memo(
  ({ text, isChatConnected, messageId, partIndex }: WebSearchApprovalProps) => {
    const { dialogueId } = useChatContext();
    // Host chat's store — useAtomAssistChat would bind to the "atom-assist"
    // store, which on project/product chats is a different, empty store.
    const streamStore = useChatStreamStore();
    const queryClient = useQueryClient();

    const [query, setQuery] = useState<string>("");
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Instant-UI mirror; the durable source of truth is the ExternalAction.
    const [localResponse, setLocalResponse] = useState<ResolvedResponse | null>(
      null,
    );
    // null = defer to the patched part's `undelivered` marker.
    const [undeliveredLocal, setUndeliveredLocal] = useState<boolean | null>(
      null,
    );
    // A pre-command 4xx (400/401/403/404/422) will never succeed; stop offering Retry.
    const [retryPermanentlyFailed, setRetryPermanentlyFailed] = useState(false);
    // Retry 202 accepted, delivery not yet confirmed by the watcher.
    const [isConfirming, setIsConfirming] = useState(false);
    const [localSearchError, setLocalSearchError] = useState<string | null>(
      null,
    );
    const lastOutcomeRef = useRef<OutcomePayload | null>(null);

    // Parse the prompt data and check for existing response (from history)
    const { promptData, existingResponse, searchResults } = useMemo(
      () => parsePromptData(text),
      [text],
    );

    // Initialize query state when promptData changes
    useEffect(() => {
      if (promptData && query === "") {
        setQuery(promptData.query);
      }
    }, [promptData, query]);

    // Durable resolution state, survives refresh and other devices.
    const {
      data: action,
      isLoading: actionLoading,
      isError: actionFetchError,
      error: actionError,
    } = useGetExternalAction(promptData?.action_id, {
      enabled: localResponse === null && existingResponse === null,
    });
    const actionResponse = useMemo<ResolvedResponse | null>(() => {
      const status = action?.status?.toLowerCase?.();
      if (status === "rejected") {
        return { approved: false, submitted_at: action?.updated_date ?? "" };
      }
      // failed = approved and executed, but the provider errored.
      if (status === "completed" || status === "failed") {
        const finalQuery = action?.data?.final_query;
        const finalMaxResults = action?.data?.final_max_results;
        return {
          approved: true,
          submitted_at: action?.updated_date ?? "",
          failed: status === "failed",
          ...(typeof finalQuery === "string" && finalQuery
            ? { final_query: finalQuery }
            : {}),
          ...(status === "failed" && action?.error_message
            ? { error: String(action.error_message) }
            : {}),
          // "in": a pre-field action (key absent) must stay unknown, not null.
          ...(action?.data && "final_recency" in action.data
            ? { final_recency: action.data.final_recency ?? null }
            : {}),
          ...(typeof finalMaxResults === "number"
            ? { final_max_results: finalMaxResults }
            : {}),
        };
      }
      // Pending, or a status this flow never produces (e.g. execution_failed
      // via the generic run route): leave the card actionable.
      return null;
    }, [action]);

    // GET failure renders an explicit unavailable state; leave a trace.
    useEffect(() => {
      if (actionFetchError) {
        console.error(
          "[WebSearchApproval:action-fetch-failed]",
          promptData?.action_id,
        );
      }
    }, [actionFetchError, promptData?.action_id]);

    // Patch user_response + search_results into the approval part (the shape
    // the backend expands on continuation); false → caller must fall back.
    const updateDialogueWithResponse = useCallback(
      (
        approved: boolean,
        finalQuery?: string,
        patchResults?: unknown,
        opts?: { undelivered?: boolean },
      ): boolean => {
        if (streamStore === null || !promptData) return false;

        let patched = false;
        streamStore
          .getState()
          .setDialogue((prevDialogue: ChatMessageWithParts[]) => {
            // Sibling branches can hold copies filterPreferredPath drops, so
            // match by action_id only when the rendered part is unknown.
            const hasStrictTarget =
              messageId !== undefined &&
              partIndex !== undefined &&
              prevDialogue.some(
                (msg) =>
                  msg.id === messageId &&
                  (msg.contentParts ?? [])[partIndex]?.type ===
                    ContentType.TOOL_RESPONSE,
              );
            return prevDialogue.map((msg) => {
              let changed = false;
              const updatedParts = (msg.contentParts ?? []).map((part, idx) => {
                if (patched || part.type !== ContentType.TOOL_RESPONSE) {
                  return part;
                }
                const isTarget = hasStrictTarget
                  ? msg.id === messageId && idx === partIndex
                  : partMatchesAction(part.text, promptData.action_id);
                if (!isTarget) return part;

                try {
                  const jsonText = stripToolResponseTags(part.text);
                  const parsed = JSON.parse(jsonText);
                  // patchResults is the approve response for a live approval.
                  const overrides = patchResults as
                    | { final_recency?: RecencyFilter | null; final_max_results?: number }
                    | undefined;
                  const outcomeKeys = {
                    user_response: {
                      approved,
                      final_query: finalQuery,
                      submitted_at: new Date().toISOString(),
                      ...(opts?.undelivered ? { undelivered: true } : {}),
                      ...(overrides && "final_recency" in overrides
                        ? { final_recency: overrides.final_recency ?? null }
                        : {}),
                      ...(typeof overrides?.final_max_results === "number"
                        ? { final_max_results: overrides.final_max_results }
                        : {}),
                    },
                    ...(patchResults ? { search_results: patchResults } : {}),
                  };
                  /*
                   * Merge at the level parsePromptData prefers: for the
                   * nested tool_result shape (what the backend annotates and
                   * every reload carries) that is the inner `result`; the
                   * wrapper keys are also written for wrapper-level readers
                   * (useLiveWebExternals). A wrapper-only write would leave
                   * a server-written inner marker masking the update.
                   */
                  let updatedContent: Record<string, unknown>;
                  if (
                    parsed?.message_type === "tool_result" &&
                    parsed.result != null
                  ) {
                    const isStringResult = typeof parsed.result === "string";
                    const inner = isStringResult
                      ? JSON.parse(parsed.result)
                      : parsed.result;
                    const newInner = { ...inner, ...outcomeKeys };
                    updatedContent = {
                      ...parsed,
                      result: isStringResult
                        ? JSON.stringify(newInner)
                        : newInner,
                      ...outcomeKeys,
                    };
                  } else {
                    updatedContent = { ...parsed, ...outcomeKeys };
                  }
                  const newText = wrapWithToolResponseTags(
                    JSON.stringify(updatedContent),
                    part.text,
                  );
                  patched = true;
                  changed = true;
                  return { ...part, text: newText };
                } catch (e) {
                  console.error(
                    "[WebSearchApproval:marker-write-failed] part JSON unparseable:",
                    e,
                  );
                  return part;
                }
              });

              return changed ? { ...msg, contentParts: updatedParts } : msg;
            });
          });
        return patched;
      },
      [messageId, partIndex, streamStore, promptData],
    );

    /*
     * Durable annotation; the endpoint resolves the outcome server-side from
     * the ExternalAction, so only the marker flag travels with the request.
     * Writes are chained so a marker persist can never be overtaken by an
     * earlier clean persist's internal 404 retry (the server replaces
     * user_response wholesale — last writer wins).
     */
    const persistChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
    const persistWebSearchOutcome = useCallback(
      (opts?: { undelivered?: boolean }): Promise<boolean> => {
        if (!promptData || !dialogueId) return Promise.resolve(false);
        const run = () =>
          persistToolResponseOutcome(dialogueId, promptData.prompt_id, {
            web_search: {
              action_id: promptData.action_id,
              ...(opts?.undelivered ? { undelivered: true } : {}),
            },
          });
        const next = persistChainRef.current.then(run, run);
        persistChainRef.current = next;
        return next;
      },
      [dialogueId, promptData],
    );

    // Inferred failure: local alert + Retry only — no patch, no persist, so a
    // false demotion can never write a durable `undelivered` on a healthy turn.
    const demoteLocally = useCallback((detail?: string) => {
      setIsConfirming(false);
      setUndeliveredLocal(true);
      setError(
        detail ? `${DELIVERY_FAILED_TEXT} (${detail})` : DELIVERY_FAILED_TEXT,
      );
    }, []);

    // Proven failure (rejected POST, thrown exception): patch + persist the
    // durable marker; the alert reflects whether the durable write landed.
    const markDeliveryFailed = useCallback(
      async (outcome: OutcomePayload, detail?: string) => {
        const patched = updateDialogueWithResponse(
          outcome.approved,
          outcome.finalQuery,
          outcome.searchResults,
          { undelivered: true },
        );
        if (!patched) {
          console.error(
            "[WebSearchApproval:marker-write-failed] undelivered patch failed",
          );
        }
        const persisted = await persistWebSearchOutcome({ undelivered: true });
        setIsConfirming(false);
        setUndeliveredLocal(true);
        if (!persisted) {
          setError(MARKER_PERSIST_FAILED_TEXT);
        } else if (!patched) {
          setError(MARKER_WRITE_FAILED_TEXT);
        } else {
          setError(
            detail
              ? `${DELIVERY_FAILED_TEXT} (${detail})`
              : DELIVERY_FAILED_TEXT,
          );
        }
      },
      [updateDialogueWithResponse, persistWebSearchOutcome],
    );

    const trackDelivery = useDeliveryWatcher();

    /*
     * Track the layer's delivery watcher and wire its verdict to this card:
     * "undelivered" demotes locally (only proven failures write the durable
     * marker); in confirm mode "delivered" also cleans the part and clears
     * the durable marker. "aborted" (user Stop, unmount) does nothing.
     */
    const watchDelivery = useCallback(
      (
        watcher: Pick<DeliveryResult, "confirmed" | "disarm">,
        outcome: OutcomePayload,
        mode: "dispatch" | "confirm" = "dispatch",
      ) => {
        void trackDelivery(watcher).then((verdict) => {
          if (verdict === "undelivered") {
            demoteLocally();
            return;
          }
          if (verdict === "delivered" && mode === "confirm") {
            // Delivery proven: clean the part and the durable marker.
            updateDialogueWithResponse(
              outcome.approved,
              outcome.finalQuery,
              outcome.searchResults,
            );
            void persistWebSearchOutcome().then((cleared) => {
              if (!cleared) setError(CLEAR_NOT_RECORDED_TEXT);
            });
            setIsConfirming(false);
            setUndeliveredLocal(false);
            setError(null);
          }
        });
      },
      [
        trackDelivery,
        updateDialogueWithResponse,
        persistWebSearchOutcome,
        demoteLocally,
      ],
    );

    // web_search outcomes require MRE#30530; #30530's extra="forbid" makes
    // future drift 422 loudly. Older backends fall back per deliverToolOutcome.
    const deliverOutcome = useCallback(
      async (patched: boolean, outcome: OutcomePayload): Promise<boolean> => {
        if (!streamStore || !promptData) return false;
        const result = await deliverToolOutcome(streamStore, {
          dialogueId,
          promptId: promptData.prompt_id,
          toolOutcome: { web_search: { action_id: promptData.action_id } },
          fallbackMessage: buildFallbackMessage(promptData.query, outcome),
          patched,
        });
        if (result.dispatch === "rejected") return false;
        watchDelivery(result, outcome);
        return true;
      },
      [streamStore, dialogueId, promptData, watchDelivery],
    );

    // Handle approval
    const handleApprove = useCallback(async () => {
      if (!promptData) return;

      setIsSubmitting(true);
      setError(null);

      try {
        const response = await approveWebSearch(promptData.action_id, query);
        void persistWebSearchOutcome().then((persisted) => {
          if (!persisted) setError(PRIMARY_PERSIST_FAILED_TEXT);
        });

        // /web-search/approve returns 200 with a web_search_error body when
        // the provider fails — the action executed, but there are no results.
        const failed =
          (response as { type?: string }).type === "web_search_error";
        if (failed) {
          const errBody = response as unknown as WebSearchErrorResponse;
          setLocalSearchError(
            errBody.error +
              (errBody.is_retryable
                ? " The assistant can retry the search."
                : ""),
          );
        }
        const outcome: OutcomePayload = {
          approved: true,
          finalQuery: query,
          searchResults: response,
        };
        lastOutcomeRef.current = outcome;
        // Include search results in the dialogue so the model can see them
        const patched = updateDialogueWithResponse(true, query, response);
        const delivered = await deliverOutcome(patched, outcome);
        if (!delivered) {
          await markDeliveryFailed(outcome);
        }
        setLocalResponse({
          approved: true,
          final_query: query,
          submitted_at: new Date().toISOString(),
          failed,
          // "in": an older backend that omits this field must leave it unset.
          ...("final_recency" in response
            ? { final_recency: response.final_recency ?? null }
            : {}),
          ...(typeof response.final_max_results === "number"
            ? { final_max_results: response.final_max_results }
            : {}),
        });
      } catch (err) {
        // The action may have settled server-side; refetch the durable
        // status so the card can recover without a reload.
        queryClient.invalidateQueries(["externalAction", promptData.action_id]);
        setError(err instanceof Error ? err.message : "Search failed");
      } finally {
        setIsSubmitting(false);
      }
    }, [
      promptData,
      query,
      persistWebSearchOutcome,
      updateDialogueWithResponse,
      deliverOutcome,
      markDeliveryFailed,
      queryClient,
    ]);

    // Handle rejection
    const handleReject = useCallback(async () => {
      if (!promptData) return;

      setIsRejecting(true);
      setError(null);

      try {
        await rejectWebSearch(promptData.action_id);
        void persistWebSearchOutcome().then((persisted) => {
          if (!persisted) setError(PRIMARY_PERSIST_FAILED_TEXT);
        });

        const outcome: OutcomePayload = { approved: false };
        lastOutcomeRef.current = outcome;
        const patched = updateDialogueWithResponse(false);
        const delivered = await deliverOutcome(patched, outcome);
        if (!delivered) {
          await markDeliveryFailed(outcome);
        }
        setLocalResponse({
          approved: false,
          submitted_at: new Date().toISOString(),
        });
      } catch (err) {
        // Mirror handleApprove: the rejection may have landed server-side.
        queryClient.invalidateQueries(["externalAction", promptData.action_id]);
        setError(err instanceof Error ? err.message : "Failed to reject");
      } finally {
        setIsRejecting(false);
      }
    }, [
      promptData,
      persistWebSearchOutcome,
      updateDialogueWithResponse,
      deliverOutcome,
      markDeliveryFailed,
      queryClient,
    ]);

    const retryDelivery = useCallback(async () => {
      if (!promptData) return;
      const prior = resolveOutcome({
        local: localResponse,
        partText: existingResponse,
        action: actionResponse,
      });
      const outcome: OutcomePayload = lastOutcomeRef.current ?? {
        approved: prior?.approved ?? true,
        finalQuery: prior?.final_query,
        searchResults,
      };
      setIsSubmitting(true);
      setError(null);
      try {
        const result =
          streamStore && dialogueId
            ? await retryToolOutcomeInjection(streamStore, {
                dialogueId,
                promptId: promptData.prompt_id,
                toolOutcome: {
                  web_search: { action_id: promptData.action_id },
                },
                fallbackMessage: buildFallbackMessage(
                  promptData.query,
                  outcome,
                ),
              })
            : ({
                kind: "failed",
                detail: "No chat session",
                permanent: true,
              } as const);
        if (result.kind === "delivered") {
          /*
           * 202 is acceptance, not delivery. Nothing is cleared here: the
           * confirm-mode watcher patches the part clean and clears the
           * durable marker only once the store shows the answer (content
           * growth or a matching ack) — so a reconnect replay can no longer
           * restore the DB marker mid-retry.
           */
          setIsConfirming(true);
          watchDelivery(
            armDeliveryWatcher(streamStore!, promptData.prompt_id),
            outcome,
            "confirm",
          );
        } else if (result.kind === "session-gone") {
          // The durable annotation already carries the outcome forward.
          updateDialogueWithResponse(
            outcome.approved,
            outcome.finalQuery,
            outcome.searchResults,
          );
          const cleared = await persistWebSearchOutcome();
          setUndeliveredLocal(false);
          setError(cleared ? SESSION_GONE_TEXT : CLEAR_NOT_RECORDED_TEXT);
        } else {
          if (result.permanent) setRetryPermanentlyFailed(true);
          await markDeliveryFailed(outcome, result.detail);
        }
      } catch (err) {
        // onClick discards the promise; an escape here must not vanish.
        console.error("[WebSearchApproval:retry-failed]", err);
        await markDeliveryFailed(outcome);
      } finally {
        setIsSubmitting(false);
      }
    }, [
      promptData,
      localResponse,
      existingResponse,
      actionResponse,
      searchResults,
      updateDialogueWithResponse,
      streamStore,
      dialogueId,
      persistWebSearchOutcome,
      markDeliveryFailed,
      watchDelivery,
    ]);

    // Check if content is complete (has closing tag, raw JSON, or stream ended)
    const isContentComplete = useMemo(() => {
      const normalizedText = text.trimStart();
      if (normalizedText.includes("</tool_response>")) return true;
      if (!normalizedText.startsWith("<tool_response>")) return true;
      return !isChatConnected;
    }, [text, isChatConnected]);

    // Null = no ChatStreamProvider above this card.
    const canAct = streamStore !== null;
    // No dialogueId yet is the healthy first-turn adoption transient
    // (PlanTemplatePublishApproval documents it) — hold silently, no caption.
    const held = !canAct && dialogueId !== undefined;
    const isResolvedView =
      resolveOutcome({
        local: localResponse,
        partText: existingResponse,
        action: actionResponse,
      }) != null;
    const heldLoggedRef = useRef(false);
    useEffect(() => {
      if (held && !isResolvedView && promptData && !heldLoggedRef.current) {
        heldLoggedRef.current = true;
        console.error("[WebSearchApproval:no-host-store] approval held", {
          dialogueId,
        });
      }
    }, [held, isResolvedView, promptData, dialogueId]);

    // Don't render if invalid data or content is still incomplete
    if (!promptData || !isContentComplete) {
      return null;
    }

    // Precedence: this session's click, part text, durable action status.
    const resolved = resolveOutcome({
      local: localResponse,
      partText: existingResponse,
      action: actionResponse,
    });
    if (resolved != null) {
      const wasApproved = resolved.approved;
      const searchFailed = Boolean(
        localResponse?.failed ??
        (existingResponse
          ? FAILURE_RESULT_TYPES.has(String(searchResults?.type))
          : actionResponse?.failed),
      );
      const failureText =
        localSearchError ??
        (typeof searchResults?.error === "string" && searchResults.error
          ? searchResults.error
          : (resolved.error ?? null));
      const undelivered =
        undeliveredLocal ?? Boolean(existingResponse?.undelivered);
      // Shown whenever known; undefined means nothing to show.
      const resolvedParamNotes: string[] = [];
      if (wasApproved) {
        if (resolved.final_recency !== undefined) {
          const recencyLabel = RECENCY_OPTIONS.find(
            (opt) => opt.value === (resolved.final_recency ?? RECENCY_NONE),
          )?.label;
          if (recencyLabel) resolvedParamNotes.push(`Recency: ${recencyLabel}`);
        }
        if (typeof resolved.final_max_results === "number") {
          resolvedParamNotes.push(`Results: ${resolved.final_max_results}`);
        }
      }
      return (
        <Box
          data-testid="web-search-submitted-state"
          sx={{
            mt: 0,
            mb: 1,
            borderRadius: "0 0 6px 6px",
            border: `1px solid ${grey[300]}`,
            borderTop: "none",
            bgcolor: grey[100],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.6,
              borderTop: `1px solid ${grey[300]}`,
              bgcolor: grey[50],
            }}
          >
            {wasApproved ? (
              searchFailed ? (
                <ErrorOutlineIcon sx={{ fontSize: 14, color: orange[700] }} />
              ) : (
                <CheckIcon sx={{ fontSize: 14, color: green[500] }} />
              )
            ) : (
              <BlockIcon sx={{ fontSize: 16, color: grey[500] }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: grey[700],
                fontWeight: 500,
                fontSize: "0.75rem",
                flex: 1,
              }}
            >
              {wasApproved
                ? `${searchFailed ? "Search failed" : "Searched"}: "${
                    resolved.final_query || promptData.query
                  }"`
                : "Web search declined"}
            </Typography>
          </Box>
          {resolvedParamNotes.length > 0 && (
            <Typography
              variant="caption"
              data-testid="web-search-resolved-params"
              sx={{
                display: "block",
                px: 1.25,
                pt: 0.5,
                color: grey[600],
                fontSize: "0.7rem",
              }}
            >
              {resolvedParamNotes.join(" · ")}
            </Typography>
          )}
          {searchFailed && failureText && (
            <Alert
              severity="warning"
              sx={{ m: 1, py: 0.5 }}
              data-testid="web-search-failed-alert"
            >
              {failureText}
            </Alert>
          )}
          {/* The marker survives remounts via the patched part; state covers this session. */}
          {isConfirming ? (
            <Alert
              severity="info"
              sx={{ m: 1, py: 0.5 }}
              data-testid="web-search-delivering-note"
            >
              {DELIVERING_TEXT}
            </Alert>
          ) : undelivered ? (
            <>
              <Alert
                severity="error"
                sx={{ m: 1, py: 0.5 }}
                data-testid="web-search-error-alert"
                action={
                  canAct ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => void retryDelivery()}
                      disabled={isSubmitting || retryPermanentlyFailed}
                      data-testid="web-search-retry-delivery"
                    >
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {(error ?? DELIVERY_FAILED_TEXT) +
                  (retryPermanentlyFailed ? RETRY_DISABLED_TEXT : "")}
              </Alert>
              {held && (
                <Typography
                  variant="caption"
                  data-testid="web-search-held-reason"
                  sx={{ display: "block", mx: 1, mb: 1, color: grey[600] }}
                >
                  {CANNOT_ACT_TEXT}
                </Typography>
              )}
            </>
          ) : (
            error && (
              <Alert
                severity="error"
                sx={{ m: 1, py: 0.5 }}
                data-testid="web-search-error-alert"
              >
                {error}
              </Alert>
            )
          )}
        </Box>
      );
    }

    // Don't flash buttons while the durable status is still being fetched.
    if (promptData.action_id && actionLoading) {
      return null;
    }

    // Unknown durable status: arming the buttons could land a resolved
    // action on a 400 dead end — say so instead.
    if (promptData.action_id && actionFetchError) {
      return (
        <Box
          data-testid="web-search-status-unavailable"
          sx={{
            mt: 0,
            mb: 1,
            borderRadius: "0 0 6px 6px",
            border: `1px solid ${grey[300]}`,
            borderTop: "none",
            bgcolor: grey[100],
          }}
        >
          <Alert severity="warning" sx={{ m: 1, py: 0.5 }}>
            {extractErrorDetail(actionError, STATUS_UNAVAILABLE_TEXT)}
          </Alert>
        </Box>
      );
    }

    const queryWasEdited = query !== promptData.query;

    return (
      <Box
        data-testid="web-search-approval-container"
        sx={{
          mt: 0,
          mb: 1,
          bgcolor: grey[100],
          border: `1px solid ${grey[300]}`,
          borderTop: "none",
          borderRadius: "0 0 6px 6px",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: grey[50],
            borderTop: `1px solid ${grey[300]}`,
            borderBottom: `1px solid ${grey[200]}`,
          }}
        >
          <PublicIcon sx={{ fontSize: 18, color: grey[600] }} />
          <Typography
            variant="body2"
            sx={{ flex: 1, fontWeight: 600, color: grey[800] }}
          >
            Web Search Request
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2 }}>
          {/* Query Display/Edit */}
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              multiline
              maxRows={4}
              autoFocus
              placeholder="Enter search query..."
              data-testid="web-search-query-input"
              data-attr="assistant:input:edit-web-search-query"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setQuery(promptData.query);
                        setIsEditing(false);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setQuery(promptData.query);
                  setIsEditing(false);
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  setIsEditing(false);
                }
              }}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                },
              }}
            />
          ) : (
            <Box
              data-testid="web-search-query-display"
              onClick={() => promptData.can_edit && setIsEditing(true)}
              sx={{
                p: 1.25,
                mb: 1.5,
                bgcolor: "white",
                border: `1px solid ${grey[200]}`,
                borderRadius: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                cursor: promptData.can_edit ? "pointer" : "default",
                transition: "all 0.15s ease",
                "&:hover": promptData.can_edit
                  ? {
                      borderColor: grey[400],
                    }
                  : {},
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: grey[500], mt: 0.125 }} />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  wordBreak: "break-word",
                  color: query ? grey[800] : grey[500],
                  fontStyle: query ? "normal" : "italic",
                }}
              >
                {query || "No query specified"}
              </Typography>
              {queryWasEdited && (
                <Chip
                  label="edited"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    bgcolor: grey[200],
                    color: grey[600],
                  }}
                />
              )}
              {promptData.can_edit && (
                <Tooltip title="Edit query">
                  <EditIcon sx={{ fontSize: 16, color: grey[400] }} />
                </Tooltip>
              )}
            </Box>
          )}

          {/* Compliance Notice */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              p: 1.25,
              mb: 1.5,
              bgcolor: alpha(grey[500], 0.08),
              borderRadius: 1,
              border: `1px solid ${grey[200]}`,
            }}
          >
            <PublicIcon
              sx={{ fontSize: 16, color: grey[600], mt: 0.125, flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              sx={{
                color: grey[700],
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              {promptData.compliance_warning}
            </Typography>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 1.5, py: 0.5 }}
              data-testid="web-search-error-alert"
            >
              {error}
            </Alert>
          )}

          {/* Held state: no host store to deliver through (see canAct). */}
          {held && (
            <Typography
              variant="caption"
              data-testid="web-search-held-reason"
              sx={{ display: "block", mb: 1, color: grey[600] }}
            >
              {CANNOT_ACT_TEXT}
            </Typography>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              onClick={handleReject}
              disabled={isSubmitting || isRejecting || !canAct}
              data-testid="web-search-cancel-button"
              data-attr="assistant:btn:reject-web-search"
              sx={{
                textTransform: "none",
                color: grey[600],
                fontSize: "0.813rem",
                px: 1.5,
                "&:hover": {
                  bgcolor: alpha(grey[500], 0.1),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApprove}
              disabled={
                isSubmitting ||
                isRejecting ||
                !query.trim() ||
                !canAct
              }
              data-testid="web-search-approve-button"
              data-attr="assistant:btn:approve-web-search"
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SearchIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                textTransform: "none",
                bgcolor: grey[800],
                fontSize: "0.813rem",
                fontWeight: 500,
                px: 2,
                "&:hover": {
                  bgcolor: grey[900],
                },
              }}
            >
              {isSubmitting ? "Searching..." : "Search"}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
);

WebSearchApproval.displayName = "WebSearchApproval";

export default WebSearchApproval;
